// app/components/Plugins/Automations/core/DataProvider.ts

import { AccountInfo, DataProviderProtocol, IndicatorResult, MarketData, Order, OrderResult } from './protocols';
import { UTCTimestamp } from 'lightweight-charts';

/**
 * Implementation of the DataProviderProtocol
 * Provides access to market data, indicators, and order execution
 */
export class DataProvider implements DataProviderProtocol {
  /**
   * Get market data for a specific ticker and timeframe
   * @param ticker The ticker symbol
   * @param timeframe The timeframe (e.g., '1m', '5m', '1h', '1d')
   */
  async getMarketData(ticker: string, timeframe: string): Promise<MarketData> {
    console.log(`Fetching market data for ${ticker} on ${timeframe} timeframe`);
    
    try {
      // Check if symbol ends with .NS, .ns, .BS, or .bs (Indian exchanges)
      const upperTicker = ticker.toUpperCase();
      if (upperTicker.endsWith('.NS') || upperTicker.endsWith('.BS')) {
        // Use Yahoo Finance for Indian stocks
        console.log(`Using Yahoo Finance API for ${ticker}`);
        return await this.getYahooFinanceData(ticker, timeframe);
      } else {
        // Use Binance for crypto and other symbols
        console.log(`Using Binance API for ${ticker}`);
        return await this.getBinanceData(ticker, timeframe);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching market data for ${ticker}: ${errorMessage}`);
      console.error('Falling back to mock data due to API error');
      
      // Return fallback mock data if API calls fail
      return this.getMockMarketData(ticker, timeframe);
    }
  }

  /**
   * Fetch data from Yahoo Finance via API route
   */
  private async getYahooFinanceData(ticker: string, timeframe: string): Promise<MarketData> {
    try {
      // Use the existing chart API route that works with Yahoo Finance
      const response = await fetch(`/api/chart?symbol=${encodeURIComponent(ticker)}&timeframe=${encodeURIComponent(timeframe)}`);
      
      if (!response.ok) {
        throw new Error(`Chart API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error(`No data found for ${ticker}`);
      }
      
      // Convert chart API data to MarketData format
      const open: number[] = [];
      const high: number[] = [];
      const low: number[] = [];
      const close: number[] = [];
      const volume: number[] = [];
      const timestamp: number[] = [];
      
      data.forEach((candle: any) => {
        if (candle.open !== null && candle.high !== null && 
            candle.low !== null && candle.close !== null) {
          open.push(candle.open);
          high.push(candle.high);
          low.push(candle.low);
          close.push(candle.close);
          volume.push(candle.volume || 0);
          // Convert date string to timestamp
          timestamp.push(new Date(candle.time).getTime());
        }
      });
      
      return {
        ticker,
        timeframe,
        open,
        high,
        low,
        close,
        volume,
        timestamp
      };
    } catch (error) {
      console.error(`Error fetching Yahoo Finance data via API: ${error}`);
      throw error;
    }
  }

  /**
   * Fetch data from Binance API
   */
  private async getBinanceData(ticker: string, timeframe: string): Promise<MarketData> {
    // Convert timeframe to Binance interval format
    const interval = this.convertTimeframeToBinanceInterval(timeframe);
    
    // Format ticker for Binance (remove any separators, ensure uppercase)
    let formattedTicker = ticker.replace('/', '').toUpperCase();
    
    // Log the formatted ticker for debugging
    console.log(`Formatted ticker for Binance API: ${formattedTicker}`);
    
    try {
      // Fetch data from Binance API
      console.log(`Requesting Binance API: https://api.binance.com/api/v3/klines?symbol=${formattedTicker}&interval=${interval}&limit=1000`);
      
      const response = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=${formattedTicker}&interval=${interval}&limit=1000`
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Binance API error (${response.status}): ${errorText}`);
        throw new Error(`Binance API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error(`No data found for ${ticker} on Binance`);
      }
      
      console.log(`Successfully retrieved ${data.length} candles from Binance for ${formattedTicker}`);
      
      // Convert Binance data to MarketData format
      const open: number[] = [];
      const high: number[] = [];
      const low: number[] = [];
      const close: number[] = [];
      const volume: number[] = [];
      const timestamp: number[] = [];
      
      data.forEach(kline => {
        timestamp.push(parseInt(kline[0]));
        open.push(parseFloat(kline[1]));
        high.push(parseFloat(kline[2]));
        low.push(parseFloat(kline[3]));
        close.push(parseFloat(kline[4]));
        volume.push(parseFloat(kline[5]));
      });
      
      return {
        ticker,
        timeframe,
        open,
        high,
        low,
        close,
        volume,
        timestamp
      };
    } catch (error) {
      console.error(`Error in Binance API call: ${error instanceof Error ? error.message : String(error)}`);
      throw error; // Re-throw to be handled by the main getMarketData method
    }
    
  }

  /**
   * Generate mock market data as fallback
   */
  private getMockMarketData(ticker: string, timeframe: string): MarketData {
    console.log(`Generating mock data for ${ticker} on ${timeframe} timeframe`);
    
    const length = 100;
    const timestamp = Array(length).fill(0).map((_, i) => Date.now() - (length - i) * this.getTimeframeMilliseconds(timeframe));
    const close = this.generatePriceArray(length, 100, 150);
    const open = close.map((price, i) => i > 0 ? close[i-1] : price * (0.9 + Math.random() * 0.2));
    const high = open.map((o, i) => Math.max(o, close[i]) * (1 + Math.random() * 0.01));
    const low = open.map((o, i) => Math.min(o, close[i]) * (1 - Math.random() * 0.01));
    const volume = Array(length).fill(0).map(() => Math.floor(Math.random() * 1000000));
    
    return {
      ticker,
      timeframe,
      open,
      high,
      low,
      close,
      volume,
      timestamp
    };
  }

  /**
   * Calculate an indicator based on market data
   * @param indicator The indicator name
   * @param params The indicator parameters
   */
  async calculateIndicator(indicator: string, params: any): Promise<IndicatorResult> {
    // This would typically calculate the indicator using a technical analysis library
    // For now, we'll return mock data
    console.log(`Calculating ${indicator} with params:`, params);
    
    const length = 100;
    const timestamp = Array(length).fill(0).map((_, i) => Date.now() - (length - i) * 60000);
    const values = this.generateIndicatorValues(indicator, length);
    
    return {
      name: indicator,
      values,
      timestamp
    };
  }

  /**
   * Execute a trading order
   * @param order The order to execute
   */
  async executeOrder(order: Order): Promise<OrderResult> {
    // This would typically send the order to a broker or exchange
    // For now, we'll return a mock result
    console.log(`Executing ${order.side} order for ${order.quantity} ${order.symbol} at ${order.price || 'market price'}`);
    
    const orderId = `order-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const filledQuantity = order.quantity;
    const averagePrice = order.price || this.getRandomPrice(order.symbol);
    
    return {
      orderId,
      status: 'FILLED',
      filledQuantity,
      averagePrice
    };
  }

  /**
   * Get account information
   */
  async getAccountInfo(): Promise<AccountInfo> {
    // This would typically fetch account data from a broker or exchange
    // For now, we'll return mock data
    console.log('Fetching account information');
    
    return {
      cash: 100000,
      positions: [
        {
          symbol: 'AAPL',
          quantity: 100,
          averagePrice: 150.25,
          marketValue: 15025
        },
        {
          symbol: 'MSFT',
          quantity: 50,
          averagePrice: 300.50,
          marketValue: 15025
        }
      ]
    };
  }

  /**
   * Helper method to generate random price array
   */
  private generatePriceArray(length: number, min: number, max: number): number[] {
    const prices: number[] = [];
    let price = min + Math.random() * (max - min);
    
    for (let i = 0; i < length; i++) {
      // Random walk
      price = price * (1 + (Math.random() - 0.5) * 0.02);
      // Keep within bounds
      price = Math.max(min, Math.min(max, price));
      prices.push(price);
    }
    
    return prices;
  }

  /**
   * Helper method to generate indicator values
   */
  private generateIndicatorValues(indicator: string, length: number): number[] {
    switch (indicator.toUpperCase()) {
      case 'RSI':
        // RSI values between 0 and 100
        return Array(length).fill(0).map(() => Math.random() * 100);
      case 'MACD':
        // MACD can be positive or negative
        return Array(length).fill(0).map(() => (Math.random() - 0.5) * 5);
      case 'SMA':
      case 'EMA':
        // Moving averages follow price
        return this.generatePriceArray(length, 100, 150);
      default:
        // Generic indicator
        return Array(length).fill(0).map(() => Math.random() * 100);
    }
  }

  /**
   * Helper method to get a random price for a symbol
   */
  private getRandomPrice(symbol: string): number {
    const basePrices: Record<string, number> = {
      'AAPL': 150,
      'MSFT': 300,
      'GOOGL': 2500,
      'AMZN': 3000,
      'TSLA': 800
    };
    
    const basePrice = basePrices[symbol] || 100;
    return basePrice * (0.95 + Math.random() * 0.1);
  }

  /**
   * Helper method to convert timeframe to milliseconds
   */
  private getTimeframeMilliseconds(timeframe: string): number {
    const unit = timeframe.charAt(timeframe.length - 1);
    const value = parseInt(timeframe.slice(0, -1));
    
    switch (unit) {
      case 'm': return value * 60 * 1000; // minutes
      case 'h': return value * 60 * 60 * 1000; // hours
      case 'd': return value * 24 * 60 * 60 * 1000; // days
      case 'w': return value * 7 * 24 * 60 * 60 * 1000; // weeks
      default: return 60 * 1000; // default to 1 minute
    }
  }

  /**
   * Convert our timeframe format to Binance interval format
   */
  private convertTimeframeToBinanceInterval(timeframe: string): string {
    // Binance uses: 1m, 3m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 8h, 12h, 1d, 3d, 1w, 1M
    const unit = timeframe.charAt(timeframe.length - 1);
    const value = parseInt(timeframe.slice(0, -1));
    
    switch (unit) {
      case 'm':
        if ([1, 3, 5, 15, 30].includes(value)) return timeframe;
        return '1h'; // Default to 1h if not supported
      case 'h':
        if ([1, 2, 4, 6, 8, 12].includes(value)) return timeframe;
        return '1h'; // Default to 1h if not supported
      case 'd':
        if (value === 1 || value === 3) return `${value}d`;
        return '1d'; // Default to 1d
      case 'w':
        return '1w';
      default:
        return '1d'; // Default to daily
    }
  }
}